<template>
    <cod-dialog
        :show.sync="showDialog"
        :drawer="drawer"
        max-width="750px"
    >
        <!-- Invoice Dialog Header -->
        <div slot="header">
            <span v-if="isEinvoice">{{ $tc('general.einvoice', 1) }}</span>
            <span v-else-if="mode === 'proforma'">{{ $tc('general.proforma', 1) }}</span>
            <span v-else>{{ $tc('general.invoice_noncash', 1) }}</span>
        </div>

        <!-- Invoice Dialog Content -->
        <div slot="content" class="invoice-content">
            <div class="pa-4">
                <div class="d-flex">
                    <v-text-field
                        ref="searchClientField"
                        class="mr-3 mt-2 mb-4"
                        :value="client ? client.name : null"
                        prepend-inner-icon="fal fa-search"
                        :placeholder="$t('invoice.search_for_customers')"
                        clearable
                        outlined
                        :filled="!!client"
                        readonly
                        hide-details
                        clear-icon="fal fa-times"
                        @click="searchClientFocus"
                        @click:clear="clearClient"
                    />

                    <v-btn
                        class="mt-4 mx-2"
                        icon
                        @click="openClientForm(client)"
                    >
                        <v-icon v-if="client">fal fa-pen</v-icon>
                        <v-icon v-else>fal fa-plus</v-icon>
                    </v-btn>
                </div>

                <v-alert
                    v-if="clientMsg"
                    class="mt-1"
                    :type="clientMsg.level"
                    text
                    transition="slide-y-transition"
                >
                    <v-row align="center">
                        <v-col class="grow">
                            <div>{{ clientMsg.text }}</div>
                        </v-col>
                        <v-col
                            v-if="clientMsg.fix"
                            class="shrink"
                        >
                            <v-btn
                                :color="clientMsg.level"
                                text
                                @click="openClientForm(client)"
                            >
                                {{ $t('general.fix') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-alert>

                <v-alert
                    v-if="invoice.taxPeriod"
                    class="mt-1"
                    type="warning"
                    text
                    transition="slide-y-transition"
                >
                    <div>{{ $t('general.tax_period_warning') }}</div>
                </v-alert>

                <v-row>
                    <v-col
                        class="pb-0"
                        cols="12"
                        :sm="mode !== 'proforma' || (mode === 'proforma' && locale === 'sr') ? 6 : 12"
                    >
                        <v-text-field
                            v-model="invoice.internalNumberSuffix"
                            class="internal-number"
                            :prefix="invoiceNumberPrefix"
                            :label="mode !== 'proforma' ? $t('invoice.number') : $t('invoice.proforma.number')"
                        />
                    </v-col>
                    <v-col
                        v-if="mode !== 'proforma'"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-text-field
                            v-model="invoice.number"
                            readonly
                            :label="$t('general.receipt_number')"
                        />
                    </v-col>
                    <v-col
                        v-else-if="locale === 'sr'"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-select
                            v-model="invoice.paymentType"
                            :items="paymentTypes"
                            item-value="value"
                            item-text="display"
                            :label="$t('general.paid_by')"
                            clear-icon="fal fa-times"
                        />
                    </v-col>
                </v-row>

                <v-textarea
                    v-model="invoice.note"
                    :label="$t('general.note')"
                    rows="3"
                />

                <v-row>
                    <v-col class="pb-0" cols="6">
                        <v-menu
                            v-model="picker2"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="invoice.startDate"
                                    :label="$t('general.start_date')"
                                    :hint="$t('invoice.period')"
                                    prepend-icon="fal fa-calendar"
                                    clearable
                                    clear-icon="fal fa-times"
                                    persistent-hint
                                    readonly
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker
                                v-model="invoice.startDate"
                                @input="picker2 = false;"
                            />
                        </v-menu>
                    </v-col>
                    <v-col class="pb-0" cols="6">
                        <v-menu
                            v-model="picker3"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="invoice.endDate"
                                    :label="$t('general.end_date')"
                                    prepend-icon="fal fa-calendar"
                                    clearable
                                    clear-icon="fal fa-times"
                                    readonly
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker
                                v-model="invoice.endDate"
                                :min="invoice.startDate"
                                @input="picker3 = false;"
                            />
                        </v-menu>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col
                        v-if="!isEinvoice"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-menu
                            v-model="picker1"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    v-model="invoice.dueDate"
                                    :label="!invoice.isAdvance ? $t('general.due_date') : $t('general.paid_date_advance')"
                                    prepend-icon="fal fa-calendar"
                                    clearable
                                    clear-icon="fal fa-times"
                                    readonly
                                    v-on="on"
                                />
                            </template>
                            <v-date-picker
                                v-model="invoice.dueDate"
                                @input="picker1 = false;"
                            />
                        </v-menu>
                    </v-col>

                    <v-col
                        v-if="locale === 'me'"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-select
                            v-model="invoice.taxPeriod"
                            :items="taxPeriods"
                            :label="$t('general.tax_period')"
                            prepend-icon="fal fa-calendar"
                            clear-icon="fal fa-times"
                            clearable
                        />
                    </v-col>

                    <v-col
                        v-if="!isEinvoice"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-checkbox
                            v-model="invoice.export"
                            :label="$t('general.export_goods')"
                            :disabled="!isAllegra || unpaidOrders.length > 0"
                        />
                    </v-col>

                    <v-col
                        v-if="enableReceiptPrinter && device.platform !== 'android'"
                        class="pb-0"
                        cols="12"
                        sm="6"
                    >
                        <v-checkbox
                            v-model="invoice.useReceiptPrinter"
                            :label="$t('general.print_on_receipt_printer')"
                        />
                    </v-col>
                </v-row>

                <div
                    v-if="isEinvoice && mode !== 'proforma'"
                    class="einvoice-options"
                >
                    <v-row>
                        <v-col
                            v-if="order && !order.refersToAdvance()"
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-checkbox
                                v-model="invoice.isAdvance"
                                :label="$t('general.advance')"
                            />
                        </v-col>

                        <v-col
                            v-if="client && client.id_type === 'tax_id_number_local_jbkjs'"
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-checkbox
                                v-model="invoice.crf"
                                :label="$t('einvoice.crf')"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.contractNumber"
                                class="my-1"
                                :label="$t('einvoice.contract_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.invoiceNumber"
                                class="my-1"
                                :label="$t('einvoice.invoice_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.agreementNumber"
                                class="my-1"
                                :label="$t('einvoice.agreement_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.receiptNumber"
                                class="my-1"
                                :label="$t('einvoice.receipt_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.dispatchNumber"
                                class="my-1"
                                :label="$t('einvoice.dispatch_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.projectNumber"
                                class="my-1"
                                :label="$t('einvoice.project_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.referenceNumber"
                                class="my-1"
                                :label="$t('einvoice.reference_number')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="invoice.referenceNumberModel"
                                class="my-1"
                                :label="$t('einvoice.reference_number_model')"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-select
                                v-model="invoice.vatObligation"
                                :items="vatObligations"
                                item-value="value"
                                item-text="display"
                                :label="$t('einvoice.vat_obligation.title')"
                                clear-icon="fal fa-times"
                            />
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                v-model="picker4"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="invoice.deliveryAt"
                                        :label="$t('einvoice.delivery_at')"
                                        prepend-icon="fal fa-calendar"
                                        persistent-hint
                                        readonly
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="invoice.deliveryAt"
                                    :max="moment().format('YYYY-MM-DD')"
                                    @input="picker4 = false;"
                                />
                            </v-menu>
                        </v-col>

                        <v-col
                            class="py-0"
                            cols="12"
                            sm="6"
                        >
                            <v-menu
                                v-model="picker5"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-model="invoice.dueDate"
                                        :label="!invoice.isAdvance ? $t('general.due_date') : $t('general.paid_date_advance')"
                                        :error-messages="invoice.vatObligation === 'by_due_date' && !invoice.dueDate ? [$t('validation.required')] : []"
                                        prepend-icon="fal fa-calendar"
                                        clearable
                                        clear-icon="fal fa-times"
                                        readonly
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="invoice.dueDate"
                                    @input="picker5 = false;"
                                />
                            </v-menu>
                        </v-col>

                        <v-col
                            class="py-0"
                            sm="6"
                            cols="12"
                        >
                            <v-file-input
                                :label="$tc('einvoice.attachments', 1)"
                                clearable
                                multiple
                                prepend-icon="fal fa-paperclip"
                                clear-icon="fal fa-times"
                                @change="encodeFiles($event)"
                            />
                        </v-col>
                    </v-row>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <v-row slot="footer">
            <v-btn
                class="flex-1 mx-1"
                color="primary"
                outlined
                @click="showDialog = false"
            >
                {{ $t('general.close') }}
            </v-btn>

            <v-btn
                class="flex-1 mx-1"
                color="primary"
                depressed
                :disabled="disableConfirm"
                @click="confirm"
            >
                <span v-if="preview">{{ $t('general.generate') }}</span>
                <span v-else>{{ confirmBtnLabel }}</span>
            </v-btn>
        </v-row>

        <!-- Invoice Dialog Drawer -->
        <div class="drawer" slot="drawer">
            <template v-if="drawer === 'selectClient'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn
                        icon
                        dark
                        @click="drawer = null"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ $tc('general.buyer', 2) }}
                    </v-toolbar-title>
                </v-toolbar>

                <div class="drawer-content">
                    <div>
                        <v-text-field
                            ref="filterClientsField"
                            v-model="filterClientQuery"
                            class="mx-4"
                            :placeholder="$t('general.search')"
                            prepend-inner-icon="far fa-search"
                            clearable
                            clear-icon="fal fa-times"
                        />
                    </div>

                    <client-list
                        ref="clientList"
                        class="flex-1"
                        :filter-query.sync="filterClientQuery"
                        :selected-client.sync="client"
                        :allow-edit="false"
                        :allow-payments="false"
                    />
                </div>
            </template>

            <template v-if="drawer === 'clientForm'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn
                        icon
                        dark
                        @click="drawer = null"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ $tc('general.buyer', 1) }}
                    </v-toolbar-title>
                </v-toolbar>

                <div class="drawer-content">
                    <client-form
                        :client="editClient"
                        @save="onClientSaved"
                    />
                </div>
            </template>

            <template v-if="drawer === 'preview'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn
                        icon
                        dark
                        @click="drawer = null"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        <span v-if="mode !== 'proforma'">{{ $tc('general.invoice_noncash', 1) }}</span>
                        <span v-else>{{ $tc('general.proforma', 1) }}</span>
                    </v-toolbar-title>

                    <v-spacer />

                    <v-btn
                        text
                        dark
                        @click="pay"
                    >
                        <v-icon class="mr-4">fal fa-check</v-icon>
                        {{ confirmBtnLabel }}
                    </v-btn>
                </v-toolbar>

                <div class="drawer-content">
                    <receipt-einvoice
                        v-if="isEinvoice || einvoiceOnly"
                        :preview="true"
                        :order="order"
                        :options="{
                            internalNumber: invoiceNumberPrefix + (invoice.internalNumberSuffix || ''),
                            isProforma: mode === 'proforma',
                            paymentType: invoice.paymentType,
                            registerRequest: einvoiceRegisterRequest,
                        }"
                    />

                    <receipt-invoice-sr
                        v-else-if="locale === 'sr'"
                        :preview="true"
                        :order="order"
                        :options="{
                            internalNumber: invoiceNumberPrefix + (invoice.internalNumberSuffix || ''),
                            isProforma: mode === 'proforma',
                            paymentType: invoice.paymentType,
                        }"
                    />

                    <receipt-invoice-me
                        v-else
                        :preview="true"
                        :order="order"
                        :options="{
                            internalNumber: invoiceNumberPrefix + (invoice.internalNumberSuffix || ''),
                            isProforma: mode === 'proforma',
                        }"
                    />
                </div>
            </template>
        </div>
    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import ClientList from '@/views/shared/components/ClientList';
import ClientForm from '@/views/shared/components/ClientForm';
import OrderService from '@/services/OrderService';
import EinvoiceService from '@/services/EinvoiceService';
import Client from '@/models/Client';
import CashRegister from '@/models/CashRegister'
import ReceiptInvoiceMe from '@/components/print/ReceiptInvoiceMe';
import ReceiptInvoiceSr from '@/components/print/ReceiptInvoiceSr';
import ReceiptEinvoice from '@/components/print/ReceiptEinvoice';
import { mapGetters } from 'vuex';

export default {
    components: {
        CodDialog,
        ClientList,
        ClientForm,
        ReceiptInvoiceMe,
        ReceiptInvoiceSr,
        ReceiptEinvoice
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        order: {
            type: Object,
            default: () => null
        },
        mode: {
            type: String,
            default: 'default'
        },
        isEinvoice: {
            type: Boolean,
            default: false
        },
        number: {
            type: String,
            default: null
        },
        preview: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        showDialog: false,
        client: null,
        editClient: null,
        filterClientQuery: null,
        drawer: null,

        invoice: {
            number: null,
            internalNumber: null,
            internalNumberSuffix: null,
            client: null,
            useReceiptPrinter: false,
            startDate: null,
            endDate: null,
            dueDate: null,
            taxPeriod: null,
            export: false,
            note: null,
            paymentType: null,
            isAdvance: false,
            crf: false,
            contractNumber: null,
            invoiceNumber: null,
            agreementNumber: null,
            receiptNumber: null,
            dispatchNumber: null,
            projectNumber: null,
            deliveryAt: null,
            attachments: [],
        },

        receiptPrinter: null,

        picker1: false,
        picker2: false,
        picker3: false,
        picker4: false,
        picker5: false,
    }),

    watch: {
        show(val) {
            this.showDialog = val;
            this.drawer = null;

            if (val) {
                this.client = Client.find(this.order.client_id);
                this.filterClientQuery = null;
                this.drawer = null;

                this.invoice.number = this.mode !== 'proforma' ? this.order.number : null;
                this.invoice.internalNumber = this.mode !== 'proforma' ? OrderService.getNextInternalNumber() : '';
                this.invoice.internalNumberSuffix = null;
                this.invoice.client = this.client;
                this.invoice.useReceiptPrinter = this.device.platform === 'android';
                this.invoice.startDate = this.order.start_date ? moment(this.order.start_date).format('YYYY-MM-DD') : null;
                this.invoice.endDate = this.order.end_date ? moment(this.order.end_date).format('YYYY-MM-DD') : null;
                this.invoice.dueDate = this.order.due_date ? moment(this.order.due_date).format('YYYY-MM-DD') : null;
                this.invoice.taxPeriod = this.order.tax_period || null;
                this.invoice.export = this.order.export || false;
                this.invoice.note = this.order.note || this.tenant.venue.invoice_note;
                this.invoice.paymentType = this.paymentTypes.length ? this.paymentTypes[0].value : null;
                this.invoice.isAdvance = false;
                this.invoice.crf = false;
                this.invoice.contractNumber = null;
                this.invoice.invoiceNumber = null;
                this.invoice.agreementNumber = null;
                this.invoice.receiptNumber = null;
                this.invoice.dispatchNumber = null;
                this.invoice.projectNumber = null;
                this.invoice.referenceNumber = null;
                this.invoice.referenceNumberModel = null;
                this.invoice.vatObligation = 'by_delivery_date';
                this.invoice.deliveryAt = moment().format('YYYY-MM-DD');
                this.invoice.attachments = [];
                this.receiptPrinter = this.$printService.getAssignedPrinter('receipt');
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },

        client(val) {
            setTimeout(() => {
                this.drawer = null;
            }, 600);

            this.invoice.client = val
            this.invoice.crf = false
        },

        'invoice.isAdvance'(val) {
            this.invoice.vatObligation = val ? 'by_due_date' : 'by_delivery_date';
        }
    },

    computed: {
        ...mapGetters(['unpaidOrders']),

        confirmBtnLabel() {
            return this.mode !== 'proforma' ? this.$t('general.confirm') : this.$t('general.save')
        },

        invoiceNumberPrefix() {
            if(this.mode === 'proforma') return ''

            return this.invoice.internalNumber + (this.invoice.internalNumberSuffix ? '/' : '')
        },

        clientValid() {
            if(!this.client) return true

            if(locale === 'me') {
                if(!this.client.country || this.client.country === 'MNE') {
                    if(!this.client.name || !this.client.tax_id_number) {
                        return false
                    }
                } else {
                    if(!this.client.name || !this.client.id_type || !this.client.id_number || !this.client.address || !this.client.city) {
                        return false
                    }
                }
            }

            return true
        },

        clientMsg() {
            if(!this.client) return null

            if(!this.clientValid) {
                return { level: 'error', text: this.$t('invoice.warning.buyer_general'), fix: true }
            }

            if(this.isEinvoice && !this.client.einvoiceAllowed()) {
                return { level: 'error', text: this.$t('invoice.warning.buyer_einvoice'), fix: true }
            }

            const localCountry = (locale === 'me' && (!this.client.country || this.client.country === 'MNE'))
                || (locale === 'sr' && this.client.country === 'SRB')
                || (locale === 'ba' && this.client.country === 'BIH')

            if(!this.invoice.export) {
                if(!localCountry) {
                    const text = this.unpaidOrders.length > 0 ? this.$t('invoice.warning.not_export') : this.$t('invoice.warning.buyer_country')
                    return { level: 'warning', text: text, fix: false }
                }

                return null
            }

            if(localCountry) {
                return { level: 'error', text: this.$t('invoice.error.buyer_country_export'), fix: true }
            }

            let message = this.$t('invoice.warning.export')
            const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation
            if(!reverseCalculation) {
                message += ' ' + this.$t('order.zero_tax_prices')
            }
            message += ' ' + this.$t('order.zero_tax')
            return { level: 'success', text: message, fix: false }
        },

        disableConfirm() {
            if(this.clientMsg && this.clientMsg.level === 'error') return true;

            if(
                this.isEinvoice
                && this.invoice.client?.id_type === 'tax_id_number_local_jbkjs'
                && !this.invoice.contractNumber
                && !this.invoice.invoiceNumber
                && !this.invoice.agreementNumber
            ) {
                return true
            };

            if(this.mode === 'proforma') {
                return !this.invoice.internalNumberSuffix
                    || (this.locale !== 'sr' && !this.invoice.client)
                    || (this.locale === 'sr' && !this.invoice.paymentType);
            }

            return !this.invoice.number || !this.invoice.client;
        },

        enableReceiptPrinter() {
            return !!this.receiptPrinter && this.device.platform !== 'web' && !this.isEinvoice;
        },

        paymentTypes() {
            return CashRegister.availablePaymentTypes().filter(type => type.value !== 'advance');
        },

        taxPeriods() {
            let result = []

            const curYear = moment().format('YYYY')
            const curYearMonths = [];
            const curMonth = moment().month();
            for(let i = 0; i < curMonth; i++) {
                curYearMonths.push(i < 9 ? ('0' + (i+1)) : ('' + (i+1)))
            }

            // Include last year
            if(curMonth < 3) {
                const lastYear = moment().add(-1, 'year').format('YYYY')
                const lastYearMonths = ['01','02','03','04','05','06','07','08','09','10','11','12'];
                result = lastYearMonths.map(month => month + '/' + lastYear)
            }

            result = [...result, ...curYearMonths.map(month => month + '/' + curYear)]

            return result
        },

        einvoiceRegisterRequest() {
            return {
                contract_number: this.invoice.contractNumber || null,
                invoice_number: this.invoice.invoiceNumber || null,
                agreement_number: this.invoice.agreementNumber || null,
                receipt_number: this.invoice.receiptNumber || null,
                dispatch_number: this.invoice.dispatchNumber || null,
                project_number: this.invoice.projectNumber || null,
                reference_number: this.invoice.referenceNumber || null,
                reference_number_model: this.invoice.referenceNumberModel || null,
                vat_obligation: this.invoice.vatObligation || 'by_delivery_date',
                delivery_at: this.invoice.deliveryAt || null,
                attachments: this.invoice.attachments || [],
                crf: this.invoice.crf
            }
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        },

        vatObligations() {
            let obligations = ['by_due_date', 'no_obligation']
            if(!this.invoice.isAdvance) {
                obligations = ['by_delivery_date', 'by_issue_date', ...obligations]
            }

            return obligations.map(obligation => {
                return {
                    value: obligation,
                    display: this.invoice.isAdvance && obligation === 'by_due_date'
                        ? this.$t(`einvoice.vat_obligation.by_due_date_advance`)
                        : this.$t(`einvoice.vat_obligation.${obligation}`)
                }
            })
        }
    },

    methods: {
        searchClientFocus() {
            this.drawer = 'selectClient';

            setTimeout(() => {
                if(this.$refs.filterClientsField) {
                    this.$refs.filterClientsField.focus();
                }
            }, 300);
        },

        openClientForm(client = null) {
            this.editClient = client
            this.drawer = 'clientForm'
        },

        clearClient() {
            this.$refs.searchClientField.blur();
            this.client = null;
        },

        confirm() {
            this.order.setClient(this.invoice.client)
            this.order.setDates(this.invoice.dueDate, this.invoice.startDate, this.invoice.endDate)
            this.order.setExport(this.invoice.export)
            this.order.setNote(this.invoice.note)
            this.order.setTaxPeriod(this.invoice.taxPeriod)

            if(this.preview) {
                this.drawer = 'preview'
                return
            }

            this.pay()
        },

        pay() {
            this.showDialog = false;

            const options = {
                internalNumber: this.invoiceNumberPrefix + (this.invoice.internalNumberSuffix || ''),
                choosePrinter: !this.invoice.useReceiptPrinter,
                forceInvoiceForm: !this.invoice.useReceiptPrinter,
                paymentType: this.mode === 'proforma'
                    ? this.invoice.paymentType
                    : (!this.invoice.isAdvance ? 'invoice' : 'advance'),
            }

            if(this.mode !== 'proforma' && this.isEinvoice) {
                options.registerRequest = this.einvoiceRegisterRequest
            }

            if(options.paymentType === 'advance') {
                options.value = [{
                    amount: this.order.getToPay().toFormat('0.0000'),
                    type: 'invoice'
                }]
            }

            if(this.mode === 'proforma') {
                this.$emit('save-order', options);
                return
            }

            this.$emit('pay-order', options);
        },

        onClientSaved(client) {
            this.client = client
            this.drawer = null
        },

        async encodeFiles(files) {
            this.invoice.attachments = [];

            if(!files || !files.length) return;

            files.forEach(async file => {
                const base64 = await Form.fileToBase64(file);
                this.invoice.attachments.push({
                    name: file.name,
                    type: file.type,
                    data: base64
                });
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    width: 100%;
}

.invoice-content {
    min-height: 600px;

    .item {
        max-height: 48px;
    }

    .selected {
        background: #efefef;

        .v-list-item__icon {
            margin: 0;
            height: 48px;
        }
    }

    ::v-deep .internal-number {
        .v-text-field__prefix {
            padding-right: 2px;
        }
    }

    .einvoice-options {
        background: #f0f0f0;
        padding: 16px;
        margin: 20px 0px 12px 0px;
        border-radius: 3px;
        border: 1px solid #959595;
    }
}

.drawer {
    display: flex;
    flex-direction: column;
    height: 100%;

    .drawer-content {
        overflow-y: auto;
        padding-top: 64px;
        display: flex;
        flex-direction: column;
        flex: 1;
    }
}
</style>
